import { Phone, Calculator, FileText, Shield } from 'lucide-react';
import { COMPANY_INFO, AI_SATEI_URL } from '@/constants/company';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const privacyJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/privacy#webpage`,
  url: `${siteUrl}/privacy`,
  name: 'プライバシーポリシー・免責事項｜株式会社クリアコーポレーション',
  description: '株式会社クリアコーポレーションのプライバシーポリシーおよび免責事項。個人情報の取り扱い・第三者提供・Cookie・AI査定の精度に関する説明。名古屋の不動産売却・買取会社。',
  inLanguage: 'ja',
  noIndex: true,
};

export default function PrivacyPage() {
  return (
    <>
      <title>プライバシーポリシー・免責事項｜株式会社クリアコーポレーション</title>
      <meta name="description" content="株式会社クリアコーポレーションのプライバシーポリシーおよび免責事項。個人情報の取り扱い・第三者提供・Cookie・AI査定の精度に関する説明。名古屋の不動産売却・買取会社。" />
      <meta name="keywords" content="クリアコーポレーション プライバシーポリシー, 個人情報保護, 名古屋 不動産 免責事項" />
      <meta property="og:title" content="プライバシーポリシー・免責事項｜株式会社クリアコーポレーション" />
      <meta property="og:description" content="株式会社クリアコーポレーションのプライバシーポリシーおよび免責事項。個人情報の取り扱いについて。" />
      <meta property="og:type" content="website" />
      <link rel="canonical" href="https://clearcorp.info/privacy" />
      <script type="application/ld+json">{JSON.stringify(privacyJsonLd)}</script>
      <Header />
      <main className="min-h-screen bg-slate-50">
        {/* Hero Section */}
        <section className="bg-slate-900 py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              プライバシーポリシー
            </h1>
            <p className="text-slate-300 text-lg">
              個人情報の取り扱いについて
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-10">
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-600 leading-relaxed mb-8">
                  株式会社クリアコーポレーション（以下「当社」）は、お客様の個人情報保護の重要性を認識し、
                  以下のプライバシーポリシー（以下「本ポリシー」）を定め、個人情報の適切な取り扱いと保護に努めます。
                </p>

                <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4 pb-2 border-b border-slate-100">
                  第1条（個人情報の定義）
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  本ポリシーにおいて「個人情報」とは、個人情報保護法にいう「個人情報」を指すものとし、
                  生存する個人に関する情報であって、当該情報に含まれる氏名、生年月日、住所、電話番号、
                  連絡先その他の記述等により特定の個人を識別できる情報を指します。
                </p>

                <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4 pb-2 border-b border-slate-100">
                  第2条（個人情報の収集方法）
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  当社は、お客様がお問い合わせフォーム、AI査定フォーム、または電話でお問い合わせいただく際に、
                  以下の個人情報を収集することがあります。
                </p>
                <ul className="list-disc list-inside text-slate-600 mt-4 space-y-2">
                  <li>氏名</li>
                  <li>メールアドレス</li>
                  <li>電話番号</li>
                  <li>住所（物件所在地を含む）</li>
                  <li>その他当社が定める入力フォームに入力された情報</li>
                </ul>

                <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4 pb-2 border-b border-slate-100">
                  第3条（個人情報を収集・利用する目的）
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  当社が個人情報を収集・利用する目的は、以下のとおりです。
                </p>
                <ol className="list-decimal list-inside text-slate-600 mt-4 space-y-2">
                  <li>お客様からのお問い合わせに対する回答および連絡のため</li>
                  <li>不動産の査定、売却、購入に関するご案内のため</li>
                  <li>当社サービスに関する情報提供のため</li>
                  <li>契約の締結および履行のため</li>
                  <li>法令に基づく対応のため</li>
                </ol>

                <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4 pb-2 border-b border-slate-100">
                  第4条（個人情報の第三者提供）
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  当社は、以下のいずれかに該当する場合を除き、お客様の同意を得ることなく、
                  第三者に個人情報を提供することはありません。
                </p>
                <ol className="list-decimal list-inside text-slate-600 mt-4 space-y-2">
                  <li>法令に基づく場合</li>
                  <li>人の生命、身体または財産の保護のために必要がある場合で、本人の同意を得ることが困難である場合</li>
                  <li>公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合で、本人の同意を得ることが困難である場合</li>
                  <li>国の機関もしくは地方公共団体またはその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合で、本人の同意を得ることにより当該事務の遂行に支障を及ぼすおそれがある場合</li>
                </ol>

                <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4 pb-2 border-b border-slate-100">
                  第5条（個人情報の安全管理）
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  当社は、個人情報の正確性及び安全性を確保するために、以下の措置を講じます。
                </p>
                <ul className="list-disc list-inside text-slate-600 mt-4 space-y-2">
                  <li>個人情報へのアクセス制限および管理の徹底</li>
                  <li>従業員への教育・研修の実施</li>
                  <li>外部からの不正アクセス防止策の実施</li>
                </ul>

                <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4 pb-2 border-b border-slate-100">
                  第6条（個人情報の開示・訂正・削除）
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  お客様は、当社に対し、ご自身の個人情報の開示、訂正、追加、削除、利用停止を請求することができます。
                  請求を希望される場合は、下記のお問い合わせ窓口までご連絡ください。
                </p>

                <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4 pb-2 border-b border-slate-100">
                  第7条（Cookieの使用）
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  当社のウェブサイトでは、お客様の利便性向上およびサイト利用状況の分析のために、
                  Cookieを使用することがあります。お客様はブラウザの設定によりCookieを無効にすることができますが、
                  その場合、一部の機能がご利用いただけなくなることがあります。
                </p>

                <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4 pb-2 border-b border-slate-100">
                  第8条（プライバシーポリシーの変更）
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  当社は、必要に応じて本ポリシーの内容を変更することがあります。
                  変更後のプライバシーポリシーは、当社ウェブサイトに掲載した時点で効力を生じるものとします。
                </p>

                <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4 pb-2 border-b border-slate-100">
                  第9条（お問い合わせ窓口）
                </h2>
                <div className="bg-slate-50 rounded-lg p-6 mt-4">
                  <p className="text-slate-900 font-semibold mb-2">株式会社クリアコーポレーション</p>
                  <p className="text-slate-600 text-sm mb-1">住所：{COMPANY_INFO.address}</p>
                  <p className="text-slate-600 text-sm mb-1">電話：{COMPANY_INFO.phone}</p>
                  <p className="text-slate-600 text-sm">受付時間：{COMPANY_INFO.businessHours}</p>
                </div>

                <div className="mt-10 pt-6 border-t border-slate-100">
                  <p className="text-slate-500 text-sm">
                    制定日：2024年4月1日
                    <br />
                    最終改定日：2024年4月1日
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Disclaimer Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-slate-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">免責事項</h2>
              </div>

              <div className="prose prose-slate max-w-none">
                <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3">
                  1. 情報の正確性について
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  当社ウェブサイトに掲載される情報には万全を期しておりますが、その正確性、完全性、有用性等について保証するものではありません。
                  掲載情報を利用することで生じたいかなる損害についても、当社は責任を負いません。
                </p>

                <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3">
                  2. AI査定について
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  AI査定は、過去の成約事例や市場データに基づく目安の価格です。実際の買取価格や売却価格とは異なる場合があります。
                  正確な価格については、現地調査を含む詳細査定が必要です。
                </p>

                <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3">
                  3. リンク先について
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  当社ウェブサイトからリンクされている第三者のウェブサイトの内容について、当社は一切の責任を負いません。
                  リンク先の利用は、お客様自身の責任において行ってください。
                </p>

                <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3">
                  4. 著作権について
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  当社ウェブサイトに掲載される文章、画像、デザイン等の著作権は、当社または正当な権利を有する第三者に帰属します。
                  無断での複製、転載、改変等は禁止しております。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-16 md:py-24 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              ご不明な点がございましたら
            </h2>
            <p className="text-slate-300 mb-8">
              お気軽にお問い合わせください。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 text-slate-900 font-semibold px-8 py-4 rounded-lg transition-colors"
              >
                お問い合わせフォーム
              </a>
              <a
                href={AI_SATEI_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-8 py-4 rounded-lg transition-colors"
              >
                <Calculator className="w-5 h-5" />
                AI査定をはじめる
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
